
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, Users, MapPin, ArrowRight, Share2, Check, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatPollDateTime } from '@/utils/dateUtils';
import { motion } from 'framer-motion';
import { SkeletonPollCard } from '@/components/ui/skeleton-card';
import { useToast } from '@/hooks/use-toast';
import { PollClub } from '@/types/poll';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ClickableAvatar } from '@/components/ui/clickable-avatar';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { getInitials } from '@/utils/stringUtils';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle, 
  AlertDialogTrigger 
} from '@/components/ui/alert-dialog';

interface PollRespondent {
  user_id: string;
  response: string;
  full_name: string;
  avatar_url: string | null;
}

interface ActivePollWithGroup {
  id: string;
  group_id: string;
  title: string;
  proposed_date: string;
  registration_deadline: string;
  club?: PollClub;
  group_name: string;
  group_avatar_url?: string;
  response_count?: number;
  available_count?: number;
  user_response?: 'available' | 'unavailable';
  created_by: string;
  is_user_admin?: boolean;
  respondents?: PollRespondent[];
}

interface ActivePollCardEnhancedProps {
  poll: ActivePollWithGroup;
  onRespond: (pollId: string, response: 'available' | 'unavailable') => Promise<boolean>;
  onWithdraw?: (pollId: string) => Promise<boolean>;
  loading?: boolean;
  index?: number;
  currentUserId?: string;
}

export const ActivePollCardEnhanced: React.FC<ActivePollCardEnhancedProps> = ({
  poll,
  onRespond,
  onWithdraw,
  loading = false,
  index = 0,
  currentUserId
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [responding, setResponding] = useState(false);
  

  if (loading) {
    return <SkeletonPollCard />;
  }

  const handleResponse = async (response: 'available' | 'unavailable') => {
    setResponding(true);
    try {
      await onRespond(poll.id, response);
    } finally {
      setResponding(false);
    }
  };

  const handleWithdraw = async () => {
    if (onWithdraw) {
      setResponding(true);
      try {
        await onWithdraw(poll.id);
      } finally {
        setResponding(false);
      }
    }
  };

  const handleViewDetails = () => {
    navigate(`/groups/${poll.group_id}?tab=polls`);
  };

  const handleShare = async () => {
    const shareUrl = `https://luvall.app/groups/${poll.group_id}?tab=polls&pollId=${poll.id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: poll.title,
          text: `Check out this poll in ${poll.group_name}!`,
          url: shareUrl,
        });
      } catch (error) {
        // User cancelled or share failed
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: "Link copied!",
        description: "Poll link copied to clipboard",
      });
    }
  };

  const isDeadlineSoon = new Date(poll.registration_deadline).getTime() - Date.now() < 24 * 60 * 60 * 1000;
  const canShare = currentUserId && (poll.created_by === currentUserId || poll.is_user_admin);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.3 }}
      whileHover={{ y: -2 }}
      className="h-full"
    >
      <Card className="w-full bg-card shadow-sm border border-border hover:shadow-md transition-all duration-200 h-full flex flex-col">
        <CardHeader className="pb-3 px-4 pt-4">
          <div className="flex items-center justify-between mb-2">
            <Badge 
              variant="secondary" 
              className="bg-padel-green-light/20 text-padel-green-dark text-xs px-2 py-1 font-medium"
            >
              {poll.group_name}
            </Badge>
            <div className="flex items-center gap-2">
              {canShare && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleShare}
                  className="h-11 w-11 p-0 hover:bg-padel-green-light/10"
                  aria-label="Share poll"
                >
                  <Share2 className="h-3.5 w-3.5 text-padel-green-dark" />
                </Button>
              )}
              {isDeadlineSoon && (
                <motion.div
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                >
                  <Badge variant="destructive" className="text-xs px-2 py-1">
                    Soon
                  </Badge>
                </motion.div>
              )}
            </div>
          </div>
          <h3 className="font-semibold text-base text-foreground line-clamp-2 leading-snug">
            {poll.title}
          </h3>
        </CardHeader>

        <CardContent className="space-y-3 px-4 pb-4 flex-1 flex flex-col">
          {/* Enhanced Date and Deadline Display */}
          <div className="space-y-2">
            <div className="flex items-center text-sm text-foreground bg-muted rounded-md p-2">
              <Calendar className="h-4 w-4 mr-2 text-padel-green-light" />
              <span className="font-medium">{formatPollDateTime(poll.proposed_date)}</span>
            </div>
            <div className="flex items-center text-xs text-muted-foreground">
              <Clock className="h-3 w-3 mr-2" />
              <span>Deadline: {formatPollDateTime(poll.registration_deadline)}</span>
            </div>
          </div>

          {/* Location from Club */}
          {poll.club?.club_name && (
            <div className="flex items-center text-sm text-muted-foreground bg-accent rounded-md p-2">
              <MapPin className="h-4 w-4 mr-2 text-accent-foreground" />
              <span className="line-clamp-1">{poll.club.club_name}</span>
            </div>
          )}

          {/* Responses summary with avatar stack */}
          {(() => {
            const available = (poll.respondents || []).filter(r => r.response === 'available');
            const unavailable = (poll.respondents || []).filter(r => r.response === 'unavailable');
            const preview = available.slice(0, 5);
            const remaining = Math.max(0, (poll.available_count || available.length) - preview.length);
            const hasAny = (poll.response_count || 0) > 0 || (poll.respondents?.length || 0) > 0;

            return (
              <Sheet>
                <SheetTrigger asChild>
                  <button
                    className="min-h-11 w-full flex items-center justify-between gap-2 bg-success/10 rounded-md p-2 hover:bg-success/15 transition-colors text-left"
                    disabled={!hasAny}
                  >
                    <div className="flex items-center text-sm text-success min-w-0">
                      <Users className="h-4 w-4 mr-2 shrink-0" />
                      <span className="font-medium truncate">
                        {poll.available_count || 0} available
                        {poll.response_count ? ` · ${poll.response_count} responses` : ''}
                      </span>
                    </div>
                    {preview.length > 0 && (
                      <div className="flex items-center -space-x-2 shrink-0">
                        {preview.map((r) => (
                          <Avatar
                            key={r.user_id}
                            className="h-6 w-6 ring-2 ring-background"
                          >
                            <AvatarImage src={r.avatar_url || undefined} alt={r.full_name} />
                            <AvatarFallback className="bg-primary/15 text-primary text-[9px] font-semibold">
                              {getInitials(r.full_name)}
                            </AvatarFallback>
                          </Avatar>
                        ))}
                        {remaining > 0 && (
                          <div className="h-6 min-w-6 px-1.5 rounded-full bg-success/20 text-success text-[10px] font-semibold flex items-center justify-center ring-2 ring-background">
                            +{remaining}
                          </div>
                        )}
                      </div>
                    )}
                  </button>
                </SheetTrigger>
                <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto">
                  <SheetHeader className="text-left">
                    <SheetTitle className="text-base">{poll.title}</SheetTitle>
                    <p className="text-xs text-muted-foreground">
                      {available.length} available · {unavailable.length} unavailable
                    </p>
                  </SheetHeader>

                  <div className="mt-4 space-y-4">
                    {available.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-success uppercase tracking-wide mb-2">
                          Available ({available.length})
                        </p>
                        <div className="space-y-1.5">
                          {available.map((r) => (
                            <div
                              key={r.user_id}
                              className="flex items-center gap-3 p-2 rounded-md bg-success/5"
                            >
                              <ClickableAvatar
                                userId={r.user_id}
                                avatarUrl={r.avatar_url || undefined}
                                fullName={r.full_name}
                                size="sm"
                              />
                              <span className="text-sm text-foreground truncate flex-1">
                                {r.full_name}
                              </span>
                              <Check className="h-4 w-4 text-success shrink-0" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {unavailable.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                          Unavailable ({unavailable.length})
                        </p>
                        <div className="space-y-1.5">
                          {unavailable.map((r) => (
                            <div
                              key={r.user_id}
                              className="flex items-center gap-3 p-2 rounded-md bg-muted/50"
                            >
                              <ClickableAvatar
                                userId={r.user_id}
                                avatarUrl={r.avatar_url || undefined}
                                fullName={r.full_name}
                                size="sm"
                              />
                              <span className="text-sm text-muted-foreground truncate flex-1">
                                {r.full_name}
                              </span>
                              <X className="h-4 w-4 text-muted-foreground shrink-0" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {available.length === 0 && unavailable.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-6">
                        No responses yet
                      </p>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            );
          })()}


          {/* Enhanced Response Buttons */}
          <div className="space-y-2 mt-auto">
            {poll.user_response === 'available' ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full min-h-11 text-sm border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                    disabled={responding}
                  >
                    {responding ? 'Withdrawing...' : 'Withdraw Response'}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Withdraw from Poll</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to withdraw your availability from this poll? You can respond again later if you change your mind.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleWithdraw}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Withdraw
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : poll.user_response === 'unavailable' ? (
              <div className="space-y-2">
                <Button
                  size="sm"
                  className="w-full min-h-11 text-sm bg-padel-green-light hover:bg-padel-green-dark text-white"
                  onClick={() => handleResponse('available')}
                  disabled={responding}
                >
                  {responding ? 'Updating...' : 'Change to Available'}
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full min-h-11 text-xs border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                      disabled={responding}
                    >
                      Withdraw Response
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Withdraw from Poll</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to withdraw your availability from this poll? You can respond again later if you change your mind.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleWithdraw}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Withdraw
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="flex-1 min-h-11 text-sm bg-padel-green-light hover:bg-padel-green-dark text-white transition-colors"
                  onClick={() => handleResponse('available')}
                  disabled={responding}
                >
                  {responding ? '...' : 'Available'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 min-h-11 text-sm border-border text-muted-foreground hover:bg-muted"
                  onClick={() => handleResponse('unavailable')}
                  disabled={responding}
                >
                  {responding ? '...' : 'Unavailable'}
                </Button>
              </div>
            )}

            {/* Enhanced View Details Button */}
            <Button
              variant="ghost"
              size="sm"
              className="w-full min-h-11 text-xs text-padel-green-dark hover:text-padel-green-dark/90 hover:bg-padel-green-light/10 flex items-center justify-center group"
              onClick={handleViewDetails}
            >
              View Details
              <ArrowRight className="h-3 w-3 ml-1 group-hover:translate-x-0.5 transition-transform" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};